import React from "react";
import { useNavigate } from "react-router-dom";
import OnboardingStepper from "./OnboardingStepper";
import ArrowLeftIcon from "../icons/ArrowLeftIcon";
import "../styles/onboardingLayout.scss";

/**
 * OnboardingLayout — Shared layout for onboarding flow
 * Figma: 1.4 Onboarding(Desktop) (24654:6649)
 *
 * Layout: Full-width, min-height 100vh
 *   Left sidebar (496px):
 *     - Logo "LOGO" (Poppins Bold 36px white)
 *     - OnboardingStepper (4 steps)
 *     - "← Back to login" link (18px Regular #c3c3c3)
 *   Right content area:
 *     - bg #0b0b0f, background pattern
 *     - Content wrapper (714px, centered, top 80px)
 *       - Back arrow (36x36)
 *       - Title + subtitle
 *       - {children} — form content
 *     - Footer links (bottom 52px)
 *
 * @param {number} currentStep - 0-indexed step for stepper
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle
 * @param {boolean} showBackArrow - Whether to show back arrow (default true)
 * @param {React.ReactNode} children - Form content
 * @param {React.ReactNode} actions - Button(s) at bottom of content
 * @param {React.ReactNode} bottomBar - Sticky bottom bar (replaces footer links)
 * @param {number} contentWidth - Content wrapper width in px (default 714)
 * @param {boolean} showFooter - Show footer links (default true, hidden when bottomBar)
 */
const OnboardingLayout = ({
  currentStep = 0,
  title,
  subtitle,
  showBackArrow = true,
  children,
  actions,
  bottomBar,
  contentWidth = 714,
  showFooter = true,
}) => {
  const navigate = useNavigate();

  return (
    <div className="onboarding-layout">
      {/* Left Sidebar — Figma: 496px, border-right #101114 */}
      <aside className="onboarding-layout__sidebar">
        {/* Sidebar background image (poster collage with overlay) */}
        <div className="onboarding-layout__sidebar-bg" />
        <div className="onboarding-layout__sidebar-wrapper">
          {/* Logo — MangoTV brand mark */}
          <div className="onboarding-layout__logo">
            <img src="/assets/logo/openplay-logo-white.png" alt="OpenPlay" style={{ height: "36px", width: "auto" }} />
          </div>

          {/* Stepper */}
          <OnboardingStepper currentStep={currentStep} />

          {/* Back to Login — Figma: ArrowLeft 24px + "Back to login" 18px #c3c3c3 */}
          <button
            className="onboarding-layout__back-to-login"
            onClick={() => navigate("/")}
          >
            <ArrowLeftIcon size={24} color="#c3c3c3" />
            <span>Back to login</span>
          </button>
        </div>
      </aside>

      {/* Right Content Area — Figma: 1424px, bg #0b0b0f */}
      <main className="onboarding-layout__content">
        {/* Background pattern (grid) */}
        <div className="onboarding-layout__bg-pattern">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="80" height="80" patternUnits="userSpaceOnUse">
                <path d="M 80 0 L 0 0 0 80" fill="none" stroke="#1a1a1e" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
            {/* Subtle dot accents */}
            <circle cx="15%" cy="8%" r="2" fill="#404040" opacity="0.5" />
            <circle cx="45%" cy="12%" r="2" fill="#404040" opacity="0.5" />
            <circle cx="75%" cy="5%" r="2" fill="#404040" opacity="0.5" />
            <circle cx="25%" cy="85%" r="2" fill="#404040" opacity="0.5" />
            <circle cx="60%" cy="90%" r="2" fill="#404040" opacity="0.5" />
          </svg>
        </div>

        {/* Content Wrapper — Figma: centered, top 80px, gap 48px, items-end */}
        <div
          className="onboarding-layout__wrapper"
          style={contentWidth !== 714 ? { width: contentWidth } : undefined}
        >
          {/* Content Section — Figma: gap 48px, items-start, full width */}
          <div className="onboarding-layout__inner">
            {/* Back Arrow — Figma: 36x36 */}
            {showBackArrow && (
              <button
                className="onboarding-layout__back-arrow"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <ArrowLeftIcon size={36} color="#fafafa" />
              </button>
            )}

            {/* Title & Subtitle — Figma: gap 8px */}
            {(title || subtitle) && (
              <div className="onboarding-layout__header">
                {title && (
                  <h1 className="onboarding-layout__title">{title}</h1>
                )}
                {subtitle && (
                  <p className="onboarding-layout__subtitle">{subtitle}</p>
                )}
              </div>
            )}

            {/* Form Content (from page) */}
            {children}
          </div>

          {/* Action Button(s) — Figma: self-aligned right (items-end on wrapper) */}
          {actions && (
            <div className="onboarding-layout__actions">{actions}</div>
          )}
        </div>

        {/* Bottom Bar (sticky, replaces footer on some pages) */}
        {bottomBar && (
          <div className="onboarding-layout__bottom-bar">{bottomBar}</div>
        )}

        {/* Footer Links — Figma: bottom 52px, centered, 715px, justify-between */}
        {showFooter && !bottomBar && (
          <nav className="onboarding-layout__footer">
            <a href="/help" className="onboarding-layout__link">Help Center</a>
            <span className="onboarding-layout__divider" />
            <a href="/terms" className="onboarding-layout__link">Terms & Condition</a>
            <span className="onboarding-layout__divider" />
            <a href="/privacy" className="onboarding-layout__link">Privacy Policy</a>
            <span className="onboarding-layout__divider" />
            <a href="/contact" className="onboarding-layout__link">Contact us</a>
          </nav>
        )}
      </main>
    </div>
  );
};

export default OnboardingLayout;
